import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SERVICES, PHONE_DISPLAY, PHONE_TEL, EMAIL } from "@/data/services";
import { SOCIALS } from "@/data/social";

export default function Footer() {
    return (
        <footer className="bg-slate-950 text-slate-300" data-testid="site-footer">
            <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
                <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <Logo dark />
                        <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-400">
                            A Calgary digital studio building websites, apps, and brands that grow businesses across Canada.
                        </p>
                        <div className="mt-6 flex gap-3">
                            {SOCIALS.map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={s.label}
                                    data-testid={`social-${s.label.toLowerCase()}`}
                                    className={`flex h-10 w-10 items-center justify-center rounded-full border border-slate-800 text-slate-400 transition-[color,border-color,transform] duration-300 hover:-translate-y-1 ${s.hover}`}
                                >
                                    <s.icon className="h-4 w-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">Services</h3>
                        <ul className="mt-5 space-y-3">
                            {SERVICES.slice(0, 6).map((s) => (
                                <li key={s.slug}>
                                    <Link
                                        to={`/contact?service=${encodeURIComponent(s.title)}`}
                                        data-testid={`footer-service-${s.slug}`}
                                        className="text-sm text-slate-400 transition-colors duration-200 hover:text-white"
                                    >
                                        {s.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">Company</h3>
                        <ul className="mt-5 space-y-3">
                            {[
                                { to: "/", label: "Home" },
                                { to: "/services", label: "Services" },
                                { to: "/work", label: "Our Work" },
                                { to: "/about", label: "About" },
                                { to: "/contact", label: "Get a Quote" },
                            ].map((l) => (
                                <li key={l.label}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                        className="text-sm text-slate-400 transition-colors duration-200 hover:text-white"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">Contact</h3>
                        <ul className="mt-5 space-y-4 text-sm">
                            <li className="flex items-start gap-3">
                                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                                <span className="text-slate-400">Calgary, Alberta, Canada</span>
                            </li>
                            <li>
                                <a href={PHONE_TEL} data-testid="footer-phone-link" className="flex items-center gap-3 text-slate-400 transition-colors duration-200 hover:text-white">
                                    <Phone className="h-4 w-4 shrink-0 text-slate-500" />
                                    {PHONE_DISPLAY}
                                </a>
                            </li>
                            <li>
                                <a href={`mailto:${EMAIL}`} data-testid="footer-email-link" className="flex items-center gap-3 break-all text-slate-400 transition-colors duration-200 hover:text-white">
                                    <Mail className="h-4 w-4 shrink-0 text-slate-500" />
                                    {EMAIL}
                                </a>
                            </li>
                        </ul>
                        <p className="mt-6 text-xs text-slate-500">English · Punjabi · Hindi</p>
                    </div>
                </div>

                <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-slate-800/80 pt-8 text-xs text-slate-500 md:flex-row md:items-center">
                    <p>© 2026 Digital Circle. All rights reserved.</p>
                    <p>Builders of LoadCircle & ServiceNext.</p>
                </div>
            </div>
        </footer>
    );
}
